import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { fetchContacts, deleteContact, markContactRead, markContactUnread } from '@/utils/api';
import { Mail, Phone, Calendar, Trash2, Eye, EyeOff, MessageSquare, User, Tag, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const data = await fetchContacts();
      setContacts(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId) => {
    try {
      await deleteContact(contactId);
      setContacts(contacts.filter(c => c.contact_id !== contactId));
      toast.success('Message supprimé avec succès');
      setDeleteConfirm(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMarkRead = async (contactId) => {
    try {
      await markContactRead(contactId);
      setContacts(contacts.map(c => 
        c.contact_id === contactId ? { ...c, read: true } : c
      ));
      toast.success('Message marqué comme lu');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleMarkUnread = async (contactId) => {
    try {
      await markContactUnread(contactId);
      setContacts(contacts.map(c => 
        c.contact_id === contactId ? { ...c, read: false } : c
      ));
      toast.success('Message marqué comme non lu');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleReadStatus = async (contact) => {
    if (contact.read) {
      await handleMarkUnread(contact.contact_id);
    } else {
      await handleMarkRead(contact.contact_id);
    }
  };

  const toggleExpand = (contactId) => {
    setExpandedId(expandedId === contactId ? null : contactId);
  };

  const unreadCount = contacts.filter(c => !c.read).length;

  return (
    <AdminLayout>
      <div data-testid="admin-contacts" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Messages de contact</h1>
            <p className="text-slate-600 mt-1">
              Gérez les messages reçus depuis le formulaire de contact
            </p>
          </div>
          {unreadCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="text-amber-600" size={18} />
              <span className="text-amber-800 font-medium">
                {unreadCount} message{unreadCount > 1 ? 's' : ''} non lu{unreadCount > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <MessageSquare className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{contacts.length}</p>
                <p className="text-sm text-slate-600">Total messages</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Mail className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{unreadCount}</p>
                <p className="text-sm text-slate-600">Non lus</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <Eye className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{contacts.length - unreadCount}</p>
                <p className="text-sm text-slate-600">Lus</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-[#127abb] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="text-slate-400" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun message</h3>
            <p className="text-slate-600">Les messages reçus depuis le formulaire de contact apparaîtront ici.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-sm font-semibold text-slate-600">
              <div className="col-span-3">Expéditeur</div>
              <div className="col-span-3">Sujet</div>
              <div className="col-span-2">Date</div>
              <div className="col-span-2">Statut</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-slate-200">
              {contacts.map((contact) => (
                <div key={contact.contact_id} data-testid={`contact-row-${contact.contact_id}`}>
                  {/* Main Row */}
                  <div 
                    className={`grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 hover:bg-slate-50 transition-colors cursor-pointer ${
                      !contact.read ? 'bg-blue-50/50' : ''
                    }`}
                    onClick={() => toggleExpand(contact.contact_id)}
                  >
                    {/* Expéditeur */}
                    <div className="col-span-3 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        !contact.read ? 'bg-[#127abb] text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        <User size={18} />
                      </div>
                      <div>
                        <p className={`font-medium ${!contact.read ? 'text-slate-900' : 'text-slate-700'}`}>
                          {contact.name}
                        </p>
                        <p className="text-sm text-slate-500 truncate max-w-[180px]">{contact.email}</p>
                      </div>
                    </div>

                    {/* Sujet */}
                    <div className="col-span-3 flex items-center">
                      <div className="flex items-center gap-2">
                        <Tag size={14} className="text-slate-400" />
                        <span className={`truncate max-w-[200px] ${!contact.read ? 'font-medium text-slate-900' : 'text-slate-700'}`}>
                          {contact.subject}
                        </span>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="col-span-2 flex items-center text-sm text-slate-600">
                      <Calendar size={14} className="mr-2 text-slate-400" />
                      {format(new Date(contact.created_at), 'dd MMM yyyy', { locale: fr })}
                    </div>

                    {/* Statut */}
                    <div className="col-span-2 flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        contact.read 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {contact.read ? 'Lu' : 'Non lu'}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleReadStatus(contact);
                        }}
                        className={`${contact.read ? 'text-slate-600 hover:text-amber-600' : 'text-slate-600 hover:text-[#127abb]'}`}
                        title={contact.read ? 'Marquer comme non lu' : 'Marquer comme lu'}
                        data-testid={`toggle-read-${contact.contact_id}`}
                      >
                        {contact.read ? <EyeOff size={16} /> : <Eye size={16} />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(contact.contact_id);
                        }}
                        className="text-slate-600 hover:text-red-600"
                        data-testid={`delete-btn-${contact.contact_id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                      {expandedId === contact.contact_id ? (
                        <ChevronUp size={16} className="text-slate-400" />
                      ) : (
                        <ChevronDown size={16} className="text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedId === contact.contact_id && (
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Coordonnées</h4>
                          <div className="space-y-2">
                            <a 
                              href={`mailto:${contact.email}`}
                              className="flex items-center gap-2 text-sm text-[#127abb] hover:underline"
                            >
                              <Mail size={14} />
                              {contact.email}
                            </a>
                            {contact.phone && (
                              <a 
                                href={`tel:${contact.phone}`}
                                className="flex items-center gap-2 text-sm text-[#127abb] hover:underline"
                              >
                                <Phone size={14} />
                                {contact.phone}
                              </a>
                            )}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Message</h4>
                          <p className="text-sm text-slate-600 whitespace-pre-wrap bg-white p-4 rounded-lg border border-slate-200">
                            {contact.message}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
                        <span className="text-xs text-slate-500">
                          Reçu le {format(new Date(contact.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                        </span>
                        <div className="flex gap-2">
                          <a href={`mailto:${contact.email}?subject=Re: ${contact.subject}`}>
                            <Button size="sm" className="bg-[#127abb] hover:bg-[#0e6292]">
                              <Mail size={14} className="mr-2" />
                              Répondre
                            </Button>
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Delete Confirmation Modal */}
                  {deleteConfirm === contact.contact_id && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
                      <div 
                        className="bg-white rounded-2xl p-6 max-w-md mx-4 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Trash2 className="text-red-600" size={24} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 text-center mb-2">
                          Supprimer ce message ?
                        </h3>
                        <p className="text-slate-600 text-center mb-6">
                          Cette action est irréversible. Le message de <strong>{contact.name}</strong> sera définitivement supprimé.
                        </p>
                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Annuler
                          </Button>
                          <Button
                            className="flex-1 bg-red-600 hover:bg-red-700"
                            onClick={() => handleDelete(contact.contact_id)}
                            data-testid={`confirm-delete-${contact.contact_id}`}
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Contacts;
